/**
 * CEO Dashboard — single-screen, ten-widget executive view.
 *
 * Layout:
 *   - PageHeader with date-range select (last 7 / 30 / 90 days, MTD)
 *   - Responsive widget grid (2 / 3 / 4 cols depending on viewport):
 *       Revenue · Cash Position · Open Orders · Margin
 *       Overdue Invoices (red-tinted) · LC Pipeline · Loan Obligations · Slow-moving Stock
 *       Top 10 Dealers · Points Liability
 *   - Below the grid: 2-col split with Recent Activity + Pending Approvals.
 *
 * Each widget click opens a Sheet with a deeper breakdown shaped by the
 * matching `*Breakdown` component.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Zap } from "lucide-react";

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@bizop/ui-kit";

import { PageHeader } from "../../components/PageHeader";
import { useMockData } from "../../lib/mockMode";
import { useHasPermission } from "../../lib/permissions";
import {
  RevenueWidget,
  RevenueBreakdown,
} from "../../components/dashboard/RevenueWidget";
import {
  MarginWidget,
  MarginBreakdown,
} from "../../components/dashboard/MarginWidget";
import {
  CashPositionWidget,
  CashPositionBreakdown,
} from "../../components/dashboard/CashPositionWidget";
import {
  OpenOrdersWidget,
  OpenOrdersBreakdown,
} from "../../components/dashboard/OpenOrdersWidget";
import {
  OverdueInvoicesWidget,
  OverdueInvoicesBreakdown,
} from "../../components/dashboard/OverdueInvoicesWidget";
import {
  LCPipelineWidget,
  LCPipelineBreakdown,
} from "../../components/dashboard/LCPipelineWidget";
import {
  LoanObligationsWidget,
  LoanObligationsBreakdown,
} from "../../components/dashboard/LoanObligationsWidget";
import {
  SlowMovingStockWidget,
  SlowMovingStockBreakdown,
} from "../../components/dashboard/SlowMovingStockWidget";
import {
  TopDealersWidget,
  TopDealersBreakdown,
} from "../../components/dashboard/TopDealersWidget";
import {
  PointsLiabilityWidget,
  PointsLiabilityBreakdown,
} from "../../components/dashboard/PointsLiabilityWidget";
import { RecentActivityFeed } from "../../components/dashboard/RecentActivityFeed";
import { PendingApprovalsCard } from "../../components/dashboard/PendingApprovalsCard";
import { QuickActionsSheet } from "../../components/dashboard/QuickActionsSheet";

export const Route = createFileRoute("/_app/")({
  component: DashboardPage,
});

type WidgetKey =
  | "revenue"
  | "margin"
  | "cash"
  | "openOrders"
  | "overdue"
  | "lc"
  | "loans"
  | "slow"
  | "topDealers"
  | "points";

const WIDGET_TITLES: Record<WidgetKey, string> = {
  revenue: "Revenue",
  margin: "Gross Margin",
  cash: "Cash Position",
  openOrders: "Open Orders",
  overdue: "Overdue Invoices",
  lc: "LC Pipeline",
  loans: "Loan Obligations",
  slow: "Slow-moving Stock",
  topDealers: "Top 10 Dealers",
  points: "Points Liability",
};

type DateRange = "7" | "30" | "90" | "mtd";

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  "7": "Last 7 days",
  "30": "Last 30 days",
  "90": "Last 90 days",
  mtd: "Month to date",
};

function DashboardPage() {
  const isMock = useMockData();
  void isMock; // mock-aware widgets read from dashboardMock; live mode comes in Phase 8

  const [range, setRange] = useState<DateRange>("30");
  const [activeWidget, setActiveWidget] = useState<WidgetKey | null>(null);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  // Finance-gated widgets are hidden from non-finance roles. Cost / margin /
  // cash / loan / overdue / LC / points-liability all leak commercial
  // sensitive data the office manager / sales / warehouse roles must not see.
  const canSeeFinance = useHasPermission("finance.read");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        description="Real-time view of revenue, margin, cash, and operational health."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setQuickActionsOpen(true)}
            >
              <Zap className="h-4 w-4" aria-hidden />
              Quick actions
            </Button>
            <Select
              value={range}
              onValueChange={(v) => setRange(v as DateRange)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(DATE_RANGE_LABELS) as [DateRange, string][]).map(
                  ([k, label]) => (
                    <SelectItem key={k} value={k}>
                      {label}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <section
        className={[
          "grid gap-4",
          "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
        ].join(" ")}
        aria-label="Executive KPIs"
      >
        {canSeeFinance ? (
          <RevenueWidget onOpen={() => setActiveWidget("revenue")} />
        ) : null}
        {canSeeFinance ? (
          <CashPositionWidget onOpen={() => setActiveWidget("cash")} />
        ) : null}
        <OpenOrdersWidget onOpen={() => setActiveWidget("openOrders")} />
        {canSeeFinance ? (
          <MarginWidget onOpen={() => setActiveWidget("margin")} />
        ) : null}
        {canSeeFinance ? (
          <OverdueInvoicesWidget onOpen={() => setActiveWidget("overdue")} />
        ) : null}
        {canSeeFinance ? (
          <LCPipelineWidget onOpen={() => setActiveWidget("lc")} />
        ) : null}
        {canSeeFinance ? (
          <LoanObligationsWidget onOpen={() => setActiveWidget("loans")} />
        ) : null}
        <SlowMovingStockWidget onOpen={() => setActiveWidget("slow")} />
        <div className="sm:col-span-2 lg:col-span-2">
          <TopDealersWidget onOpen={() => setActiveWidget("topDealers")} />
        </div>
        {canSeeFinance ? (
          <div className="sm:col-span-2 lg:col-span-2">
            <PointsLiabilityWidget onOpen={() => setActiveWidget("points")} />
          </div>
        ) : null}
      </section>

      <section
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        aria-label="Activity"
      >
        <RecentActivityFeed />
        <PendingApprovalsCard />
      </section>

      <QuickActionsSheet
        open={quickActionsOpen}
        onOpenChange={setQuickActionsOpen}
      />

      <Sheet
        open={activeWidget !== null}
        onOpenChange={(open) => {
          if (!open) setActiveWidget(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {activeWidget ? (
            <>
              <SheetHeader>
                <SheetTitle>{WIDGET_TITLES[activeWidget]}</SheetTitle>
                <SheetDescription>
                  Breakdown for {DATE_RANGE_LABELS[range].toLowerCase()}
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <BreakdownPicker widget={activeWidget} />
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function BreakdownPicker({ widget }: { widget: WidgetKey }) {
  switch (widget) {
    case "revenue":
      return <RevenueBreakdown />;
    case "margin":
      return <MarginBreakdown />;
    case "cash":
      return <CashPositionBreakdown />;
    case "openOrders":
      return <OpenOrdersBreakdown />;
    case "overdue":
      return <OverdueInvoicesBreakdown />;
    case "lc":
      return <LCPipelineBreakdown />;
    case "loans":
      return <LoanObligationsBreakdown />;
    case "slow":
      return <SlowMovingStockBreakdown />;
    case "topDealers":
      return <TopDealersBreakdown />;
    case "points":
      return <PointsLiabilityBreakdown />;
    default:
      return null;
  }
}
